import React, { useMemo, useState } from 'react';
import {
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import DateTimePicker, {
  type DateTimePickerEvent,
} from '@react-native-community/datetimepicker';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import {
  parseCalendarDateInput,
  toCalendarDateString,
} from '../utils/dateTime';

type Props = {
  open: boolean;
  value: string;
  onClose: () => void;
  onConfirm: (value: string) => void;
  minimumDate?: Date;
  maximumDate?: Date;
};

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function defaultPickerDate(value: string): Date {
  return parseCalendarDateInput(value) ?? new Date();
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1, 12, 0, 0, 0);
}

function shiftMonth(date: Date, delta: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + delta, 1, 12, 0, 0, 0);
}

function dateKey(date: Date): string {
  return toCalendarDateString(date);
}

function isOutOfRange(date: Date, minimumDate?: Date, maximumDate?: Date): boolean {
  const key = dateKey(date);
  if (minimumDate && key < dateKey(minimumDate)) return true;
  if (maximumDate && key > dateKey(maximumDate)) return true;
  return false;
}

function monthCells(visibleMonth: Date): Array<Date | null> {
  const first = startOfMonth(visibleMonth);
  const mondayPad = (first.getDay() + 6) % 7;
  const lastDay = new Date(
    visibleMonth.getFullYear(),
    visibleMonth.getMonth() + 1,
    0,
    12,
    0,
    0,
    0,
  ).getDate();
  const cells: Array<Date | null> = [];
  for (let i = 0; i < mondayPad; i += 1) cells.push(null);
  for (let day = 1; day <= lastDay; day += 1) {
    cells.push(
      new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), day, 12, 0, 0, 0),
    );
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function WebCalendarModal({
  value,
  onClose,
  onConfirm,
  minimumDate,
  maximumDate,
}: Omit<Props, 'open'>) {
  const palette = useAppColors();
  const selected = defaultPickerDate(value);
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(selected));
  const cells = useMemo(() => monthCells(visibleMonth), [visibleMonth]);
  const selectedKey = value.trim() ? dateKey(selected) : '';
  const monthTitle = visibleMonth.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
  const weeks: Array<Array<Date | null>> = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }

  return (
    <Modal transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.webRoot}>
        <Pressable
          style={styles.webOverlay}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Dismiss date picker"
        />
        <View
          accessibilityRole="summary"
          accessibilityLabel="Date picker"
          style={[styles.webSheet, { backgroundColor: palette.surface }]}
        >
          <View style={styles.webToolbar}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Previous month"
              onPress={() => setVisibleMonth(current => shiftMonth(current, -1))}
              style={[styles.webNavButton, { cursor: 'pointer' as const }]}
            >
              <Text style={[styles.webNavLabel, { color: palette.primary }]}>‹</Text>
            </Pressable>
            <Text style={[styles.webMonthTitle, { color: palette.textPrimary }]}>
              {monthTitle}
            </Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Next month"
              onPress={() => setVisibleMonth(current => shiftMonth(current, 1))}
              style={[styles.webNavButton, { cursor: 'pointer' as const }]}
            >
              <Text style={[styles.webNavLabel, { color: palette.primary }]}>›</Text>
            </Pressable>
          </View>
          <View style={styles.webWeekRow}>
            {WEEKDAY_LABELS.map(label => (
              <Text
                key={label}
                style={[styles.webWeekday, { color: palette.textMuted }]}
              >
                {label}
              </Text>
            ))}
          </View>
          {weeks.map((week, weekIndex) => (
            <View key={weekIndex} style={styles.webWeekRow}>
              {week.map((day, dayIndex) => {
                if (!day) {
                  return <View key={`empty-${weekIndex}-${dayIndex}`} style={styles.webDayCell} />;
                }
                const key = dateKey(day);
                const selectedDay = key === selectedKey;
                const disabled = isOutOfRange(day, minimumDate, maximumDate);
                return (
                  <Pressable
                    key={key}
                    accessibilityRole="button"
                    accessibilityLabel={day.toLocaleDateString('en-US', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                    accessibilityState={{ selected: selectedDay, disabled }}
                    disabled={disabled}
                    onPress={() => onConfirm(key)}
                    style={[
                      styles.webDayCell,
                      { cursor: disabled ? 'default' : ('pointer' as const) },
                      selectedDay
                        ? { backgroundColor: palette.primary, borderRadius: 8 }
                        : null,
                    ]}
                  >
                    <Text
                      style={[
                        styles.webDayLabel,
                        {
                          color: disabled
                            ? palette.textMuted
                            : selectedDay
                              ? palette.onPrimary
                              : palette.textPrimary,
                        },
                      ]}
                    >
                      {day.getDate()}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          ))}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Cancel"
            onPress={onClose}
            style={[styles.webCancel, { cursor: 'pointer' as const }]}
          >
            <Text style={[styles.iosAction, { color: palette.textSecondary }]}>
              Cancel
            </Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

/**
 * Platform calendar picker UI. Parent owns open state and the pressable field.
 * Confirmed values are always YYYY-MM-DD.
 */
export function CalendarDatePicker({
  open,
  value,
  onClose,
  onConfirm,
  minimumDate,
  maximumDate,
}: Props) {
  const palette = useAppColors();
  const [iosDraft, setIosDraft] = useState(() => defaultPickerDate(value));
  const isWeb = (Platform.OS as string) === 'web';

  React.useEffect(() => {
    if (open) setIosDraft(defaultPickerDate(value));
  }, [open, value]);

  if (!open) return null;

  const commitDate = (date: Date) => {
    onConfirm(toCalendarDateString(date));
  };

  const onAndroidChange = (event: DateTimePickerEvent, date?: Date) => {
    if (event.type === 'dismissed') {
      onClose();
      return;
    }
    if (event.type === 'set' && date) {
      commitDate(date);
      return;
    }
    onClose();
  };

  if (isWeb) {
    return (
      <WebCalendarModal
        value={value}
        onClose={onClose}
        onConfirm={onConfirm}
        minimumDate={minimumDate}
        maximumDate={maximumDate}
      />
    );
  }

  if (Platform.OS === 'android') {
    return (
      <DateTimePicker
        value={defaultPickerDate(value)}
        mode="date"
        display="calendar"
        minimumDate={minimumDate}
        maximumDate={maximumDate}
        onChange={onAndroidChange}
      />
    );
  }

  if (Platform.OS === 'ios') {
    return (
      <Modal transparent animationType="slide" onRequestClose={onClose}>
        <View style={styles.iosRoot}>
          <Pressable
            style={styles.iosOverlay}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Dismiss date picker"
          />
          <View style={[styles.iosSheet, { backgroundColor: palette.surface }]}>
            <View style={styles.iosToolbar}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Cancel"
                onPress={onClose}
              >
                <Text style={[styles.iosAction, { color: palette.textSecondary }]}>
                  Cancel
                </Text>
              </Pressable>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Done"
                onPress={() => commitDate(iosDraft)}
              >
                <Text style={[styles.iosAction, { color: palette.primary }]}>
                  Done
                </Text>
              </Pressable>
            </View>
            <DateTimePicker
              value={iosDraft}
              mode="date"
              display="spinner"
              minimumDate={minimumDate}
              maximumDate={maximumDate}
              onChange={(_event, date) => {
                if (date) setIosDraft(date);
              }}
            />
          </View>
        </View>
      </Modal>
    );
  }

  return null;
}

const styles = StyleSheet.create({
  iosRoot: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  iosOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: 'rgba(0,0,0,0.35)',
  },
  iosSheet: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingBottom: spacing.xl,
  },
  iosToolbar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  iosAction: {
    ...typography.body,
    fontWeight: '600',
  },
  webRoot: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
    zIndex: 1000,
  },
  webOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: 'rgba(0,0,0,0.35)',
  },
  webSheet: {
    width: '100%',
    maxWidth: 360,
    borderRadius: 16,
    padding: spacing.lg,
    zIndex: 1,
  },
  webToolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.md,
  },
  webNavButton: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  webNavLabel: {
    fontSize: 28,
    lineHeight: 32,
    fontWeight: '600',
  },
  webMonthTitle: {
    ...typography.sectionTitle,
  },
  webWeekRow: {
    flexDirection: 'row',
  },
  webWeekday: {
    ...typography.caption,
    flex: 1,
    textAlign: 'center',
    fontWeight: '600',
    paddingVertical: spacing.xs,
  },
  webDayCell: {
    flex: 1,
    minHeight: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  webDayLabel: {
    ...typography.body,
    fontWeight: '600',
  },
  webCancel: {
    alignSelf: 'flex-end',
    marginTop: spacing.md,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
  },
});
